import { BigNumber } from '@ethersproject/bignumber';
import { formatEther } from '@ethersproject/units';
import { CaretDown, Check } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { formatUSD } from '@/common/currency';
import networkCheck from '@/common/networkCheck';
import { getNetworkLogo } from '@/common/networkUtils';
import { GetQuoteParametersWithoutChainId } from '@/common/types';
import AccordionSection from '@/components/AccordionSection';
import { useConvertUSD } from '@/hooks/useConvertUSD';
import { useRelayFees } from '@/hooks/useRelayMint';
import { useUserBalance } from '@/hooks/useUserBalance';
import useMainStore from '@/store/mainStore';

/**
 * The relay network dropdown component.
 * This component will display the available Relay networks and the associated fees.
 * @param availableNetworks - The available Relay networks to display in the dropdown
 * @param selectedNetworkId - The network id of the selected network
 * @param callConfig - The Relay call config
 * @param expanded - Whether the dropdown is expanded
 * @param setExpanded - The function to set the expanded state
 * @param onSelect - The function to call when a network is selected
 */
export default function RelayNetworkDropdown({
  availableNetworks,
  selectedNetworkId,
  parameters,
  expanded,
  setExpanded,
  onSelect,
  onError,
  columnHeader1 = 'Pay With',
  columnHeader2 = 'Balance',
}: {
  availableNetworks: number[];
  selectedNetworkId: number;
  parameters: GetQuoteParametersWithoutChainId;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  onSelect?: (network: number) => void;
  onError?: (error: string) => void;
  columnHeader1?: string;
  columnHeader2?: string;
}) {
  const {
    fees,
    loading: pricesLoading,
    fetchFees,
    error: feesError,
  } = useRelayFees(availableNetworks, parameters);
  const [isRelayNetworkLoading, setIsRelayNetworkLoading] = useState(false);
  const { balance, isLoading: balanceLoading } = useUserBalance();
  const address = useMainStore((state) => state.walletAddress);
  const connectedNetwork = useMainStore((state) => state.connectedNetwork);
  const [error, setError] = useState<string>();

  const currentUserBalance = useMemo(() => {
    return balance?.[`${connectedNetwork}-${address}`];
  }, [balance, connectedNetwork, address]);

  useEffect(() => {
    setError(undefined);
    // If user is on an unsupported network, we want to show an error
    if (connectedNetwork && !availableNetworks.includes(connectedNetwork)) {
      handleError(
        `You are on an unsupported network, please choose eligible network from the dropdown`,
      );
      return;
    }

    // Notify user there is a relay error on user connected network
    if (connectedNetwork && feesError[connectedNetwork]) {
      handleError(
        `Unable to execute transaction on the current network, please try another network below`,
      );
      return;
    }

    // If user does not have enough balance, we want to show an error
    if (connectedNetwork && currentUserBalance && fees?.[connectedNetwork]) {
      const totalFees = BigNumber.from(fees[connectedNetwork]?.gasFee?.raw || '0')
        .add(BigNumber.from(fees[connectedNetwork]?.bridgeFee?.raw || '0'))
        .add(BigNumber.from(parameters.amount || '0'));

      if (currentUserBalance.balance.lt(totalFees)) {
        handleError(`Insufficient balance`);
        return;
      }
    }
  }, [connectedNetwork, fees, balance, address]);

  /**
   * Refetch fees every 20 seconds so we get fresh prices from relay networks
   */
  useEffect(() => {
    const interval = setInterval(() => {
      fetchFees();
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchFees]);

  /**
   * On relay network select, we want to ask user to switch to the selected network.
   * This server 2 purposes:
   * + We can check their balances and see if they have enough to execute the relay transactions
   * + User need to be on this network to execute the relay transactions
   */
  const onNetworkSelect = useCallback(async (networkId: number) => {
    setIsRelayNetworkLoading(true);

    onSelect?.(networkId);
    setError(undefined);

    try {
      await networkCheck(networkId);
    } catch (e) {
      console.error('Failed to select the network', e);
      handleError(
        `Failed to select the ${StringUtils.capitalizeFirstLetter(
          Network.getDisplayNameForNetworkId(networkId),
        )} network. To pay using the ${StringUtils.capitalizeFirstLetter(
          Network.getDisplayNameForNetworkId(networkId),
        )} network, open your wallet and switch to the ${StringUtils.capitalizeFirstLetter(
          Network.getDisplayNameForNetworkId(networkId),
        )} network.`,
      );
    }

    setIsRelayNetworkLoading(false);
  }, []);

  const handleError = useCallback(
    (err: string) => {
      // if there is an error, we want to expand the dropdown to show user other options
      setError(err);
      // notify parent component of the error
      onError?.(err);
    },
    [onError],
  );

  const renderTitle = () => {
    return (
      <button
        className={`m-claim--flex m-claim--w-full m-claim--flex-col m-claim--items-center m-claim--rounded-lg m-claim--border m-claim--px-4 m-claim--py-2 m-claim--shadow-sm hover:m-claim--bg-surface-secondary focus:m-claim--outline-none focus-visible:m-claim--outline-none ${balanceLoading ? 'm-claim--cursor-not-allowed m-claim--opacity-50' : ''}`}
        disabled={balanceLoading}
        aria-label="Select network dropdown"
        tabIndex={0}
      >
        {/* Headers */}
        <div className="m-claim--flex m-claim--w-full m-claim--items-center m-claim--justify-between">
          <div className="m-claim--text-xs m-claim--font-semibold m-claim--text-secondary">
            {columnHeader1}
          </div>
          <div className="m-claim--py-2 m-claim--text-xs m-claim--font-semibold m-claim--text-secondary">
            {columnHeader2}
          </div>
        </div>

        {/* Selected network and balance */}
        <div className="m-claim--flex m-claim--h-10 m-claim--w-full m-claim--flex-row m-claim--items-center m-claim--justify-between">
          {balanceLoading ? (
            <Skeleton
              width={400}
              height={48}
            />
          ) : (
            <>
              <NetworkDisplay networkId={selectedNetworkId} />
              {currentUserBalance && (
                <PriceDisplay
                  price={currentUserBalance.balance}
                  networkId={selectedNetworkId}
                />
              )}
            </>
          )}
        </div>

        {/* CaretDown icon at the bottom */}
        <div className="m-claim--flex m-claim--w-full m-claim--justify-center">
          <CaretDown
            size={18}
            className="m-claim--font-bold m-claim--text-primary"
            aria-label="Expand dropdown"
          />
        </div>
      </button>
    );
  };

  if (isRelayNetworkLoading) {
    return <Skeleton height={68} />;
  }

  return (
    <AccordionSection
      title={renderTitle()}
      controlledConfig={{
        expanded: expanded || !!error, // if there is an error, we want to expand the dropdown to show user other options
        onToggle: setExpanded,
      }}
    >
      <div className="m-claim--z-50 m-claim--mt-2 m-claim--w-full m-claim--rounded-lg m-claim--border m-claim--border-default m-claim--bg-surface-secondary m-claim--shadow-lg">
        <div className="m-claim--flex m-claim--w-full m-claim--items-center m-claim--justify-between">
          <div className="m-claim--px-4 m-claim--py-2 m-claim--text-xs m-claim--font-semibold m-claim--text-secondary">
            Network
          </div>
          <div className="m-claim--ml-auto m-claim--px-4 m-claim--py-2 m-claim--text-xs m-claim--font-semibold m-claim--text-secondary">
            Bridge Fee
          </div>
        </div>

        {availableNetworks
          .filter((networkId) => !feesError[networkId])
          .map((networkId) => (
            <button
              key={networkId}
              className={`m-claim--focus:m-claim--bg-surface-contrast ${networkId === selectedNetworkId ? 'm-claim--bg-surface-accent/80' : ''} m-claim--flex m-claim--w-full m-claim--items-center m-claim--justify-between m-claim--px-4 m-claim--py-2 hover:m-claim--bg-surface-accent `}
              onClick={() => {
                onNetworkSelect(networkId);
                setExpanded(false);
              }}
            >
              <NetworkDisplay
                networkId={networkId}
                selected={networkId === selectedNetworkId}
              />
              {networkId === parameters.toChainId ? (
                <div className="m-claim--text-sm m-claim--text-muted">None</div>
              ) : pricesLoading[networkId] ? (
                <Skeleton />
              ) : fees[networkId]?.bridgeFee?.raw !== undefined ? (
                <PriceDisplay
                  price={
                    networkId !== parameters.toChainId
                      ? BigNumber.from(fees[networkId]?.bridgeFee?.raw)
                      : BigNumber.from(0)
                  }
                  networkId={networkId}
                />
              ) : (
                '--'
              )}
            </button>
          ))}
      </div>
    </AccordionSection>
  );
}

function NetworkDisplay({ networkId, selected }: { networkId: number; selected?: boolean }) {
  return Network.SUPPORTED_NETWORKS.includes(networkId) ? (
    <div className={`m-claim--flex m-claim--items-center m-claim--gap-2 `}>
      <img
        src={getNetworkLogo(networkId)}
        alt={StringUtils.capitalizeFirstLetter(Network.getDisplayNameForNetworkId(networkId))}
        className={` m-claim--size-5`}
      />
      <span className={`${selected ? 'm-claim--font-bold' : ''}`}>
        {StringUtils.capitalizeFirstLetter(Network.getDisplayNameForNetworkId(networkId))}
      </span>
      {selected && <Check size={14} />}
    </div>
  ) : (
    <span className="m-claim--text-muted">Unsupported</span>
  );
}

function PriceDisplay({ price, networkId }: { price: BigNumber; networkId: number }) {
  const { usdValue, isLoading } = useConvertUSD({ price: price || BigNumber.from(0), networkId });
  return (
    <span className={`m-claim--text-right m-claim--text-sm m-claim--tracking-wide`}>
      {price.gt(BigNumber.from('100000000000000')) && parseFloat(formatEther(price)).toFixed(4)}

      <div className="m-claim--flex m-claim--justify-end  m-claim--text-muted">
        {isLoading ? (
          <Skeleton width={60} />
        ) : (
          <span>{`${price.isZero() ? '' : '~'} ${formatUSD(usdValue ?? 0)} USD`}</span>
        )}
      </div>
    </span>
  );
}
